import type { AnalyticsZone } from "./access.ts";
import type { CaptureSignalName } from "./capture.ts";

export const ANALYTICS_METRICS_PATH = "/api/analytics/metrics";

export type CaptureSignals = readonly [CaptureSignalName, ...CaptureSignalName[]];

export const METRIC_UNITS = ["count", "seconds", "ratio"] as const;

type MetricUnit = (typeof METRIC_UNITS)[number];

export const METRIC_AGGREGATES = ["sum", "peak", "mean", "share", "last", "distinct"] as const;

export type MetricAggregate = (typeof METRIC_AGGREGATES)[number];

export const METRIC_DIMENSIONS = [
  "time",
  "channel",
  "member",
  "weekday",
  "hour",
  "segment",
  "source",
  "state",
  "content",
  "app",
  "participation",
  "locale",
] as const;

export type MetricDimension = (typeof METRIC_DIMENSIONS)[number];

export const METRIC_FILTERS = ["roles", "channels", "channel", "member"] as const;

export type MetricFilter = (typeof METRIC_FILTERS)[number];

export const METRIC_FILTER_PARAMS = {
  roles: ["rolesInclude", "rolesExclude"],
  channels: ["channelsInclude", "channelsExclude"],
  channel: ["channelId"],
  member: ["userId"],
} as const satisfies Record<MetricFilter, readonly string[]>;

export const RANKED_DIMENSIONS = [
  "channel",
  "member",
  "app",
  "locale",
] as const satisfies readonly MetricDimension[];

export type RankedDimension = (typeof RANKED_DIMENSIONS)[number];

export const METRIC_LIMIT_MAX = 200;

export const ENTITY_ID_LIMIT = 200;

export const MEMBER_ZONE: AnalyticsZone = "profiles";

export const PULSE_MOVES = ["up", "down"] as const;

export type Move = (typeof PULSE_MOVES)[number];

export type Better = Move | null;

export type Outlook = "good" | "bad" | "neutral";

export const moveTone = (direction: Move | "flat", better: Better): Outlook =>
  direction === "flat" || better === null ? "neutral" : direction === better ? "good" : "bad";

export type MetricEntry = {
  readonly signals: CaptureSignals;
  readonly zone: AnalyticsZone;
  readonly unit: MetricUnit;
  readonly better: Better;
  readonly aggregate: MetricAggregate;
  readonly groupings: readonly (readonly MetricDimension[])[];
  readonly filters: readonly MetricFilter[];
  readonly bounded?: true;
};

export const ANALYTICS_METRICS = {
  members: {
    signals: ["messages"],
    zone: "growth",
    unit: "count",
    better: "up",
    aggregate: "last",
    groupings: [["time"]],
    filters: [],
  },
  boosts: {
    signals: ["messages"],
    zone: "growth",
    unit: "count",
    better: "up",
    aggregate: "last",
    groupings: [["time"]],
    filters: [],
  },
  joins: {
    signals: ["member_events"],
    zone: "growth",
    unit: "count",
    better: "up",
    aggregate: "sum",
    groupings: [["time"], ["weekday", "hour"], ["time", "source"]],
    filters: ["roles"],
  },
  rejoins: {
    signals: ["member_events"],
    zone: "growth",
    unit: "count",
    better: "up",
    aggregate: "sum",
    groupings: [["time"]],
    filters: ["roles"],
  },
  leaves: {
    signals: ["member_events"],
    zone: "growth",
    unit: "count",
    better: "down",
    aggregate: "sum",
    groupings: [["time"], ["time", "segment"]],
    filters: ["roles"],
  },
  net_growth: {
    signals: ["member_events"],
    zone: "growth",
    unit: "count",
    better: "up",
    aggregate: "sum",
    groupings: [["time"]],
    filters: ["roles"],
  },
  messages: {
    signals: ["messages"],
    zone: "engagement",
    unit: "count",
    better: "up",
    aggregate: "sum",
    groupings: [["time"], ["channel"], ["member"], ["weekday"], ["weekday", "hour"]],
    filters: ["roles", "channels", "channel", "member"],
  },
  classified_messages: {
    signals: ["messages", "message_content"],
    zone: "engagement",
    unit: "count",
    better: null,
    aggregate: "sum",
    groupings: [["time", "content"]],
    filters: ["roles", "channels", "channel", "member"],
  },
  reply_share: {
    signals: ["messages"],
    zone: "engagement",
    unit: "ratio",
    better: "up",
    aggregate: "share",
    groupings: [["time"]],
    filters: ["roles", "channels"],
  },
  reactions_given: {
    signals: ["reactions"],
    zone: "engagement",
    unit: "count",
    better: "up",
    aggregate: "sum",
    groupings: [["time"], ["member"]],
    filters: ["roles", "channels"],
  },
  reactions_received: {
    signals: ["reactions"],
    zone: "engagement",
    unit: "count",
    better: "up",
    aggregate: "sum",
    groupings: [["time"], ["member"]],
    filters: ["roles", "channels"],
  },
  deleted_messages: {
    signals: ["moderation"],
    zone: "engagement",
    unit: "count",
    better: "down",
    aggregate: "sum",
    groupings: [["time"], ["channel"]],
    filters: ["channels", "channel"],
  },
  timeouts: {
    signals: ["moderation", "member_roster"],
    zone: "engagement",
    unit: "count",
    better: "down",
    aggregate: "sum",
    groupings: [["time"]],
    filters: [],
  },
  bans: {
    signals: ["moderation"],
    zone: "engagement",
    unit: "count",
    better: "down",
    aggregate: "sum",
    groupings: [["time"]],
    filters: [],
  },
  threads: {
    signals: ["threads"],
    zone: "engagement",
    unit: "count",
    better: "up",
    aggregate: "sum",
    groupings: [["channel"]],
    filters: ["roles", "channels"],
  },
  voice: {
    signals: ["voice"],
    zone: "engagement",
    unit: "seconds",
    better: "up",
    aggregate: "sum",
    groupings: [["time"], ["channel"], ["member"], ["weekday", "hour"], ["time", "state"]],
    filters: ["roles", "channels", "channel", "member"],
  },
  streaming: {
    signals: ["voice"],
    zone: "engagement",
    unit: "seconds",
    better: "up",
    aggregate: "sum",
    groupings: [["time"]],
    filters: ["roles", "channels"],
  },
  camera: {
    signals: ["voice"],
    zone: "engagement",
    unit: "seconds",
    better: "up",
    aggregate: "sum",
    groupings: [["time"]],
    filters: ["roles", "channels"],
  },
  apps: {
    signals: ["app_activity"],
    zone: "audience",
    unit: "seconds",
    better: null,
    aggregate: "sum",
    groupings: [["time", "app"]],
    filters: ["roles", "member"],
  },
  online: {
    signals: ["presence_gauge"],
    zone: "audience",
    unit: "count",
    better: "up",
    aggregate: "mean",
    groupings: [["time"], ["hour"]],
    filters: [],
  },
  idle: {
    signals: ["presence_gauge"],
    zone: "audience",
    unit: "count",
    better: null,
    aggregate: "mean",
    groupings: [["time"]],
    filters: [],
  },
  dnd: {
    signals: ["presence_gauge"],
    zone: "audience",
    unit: "count",
    better: null,
    aggregate: "mean",
    groupings: [["time"]],
    filters: [],
  },
  offline: {
    signals: ["presence_gauge"],
    zone: "audience",
    unit: "count",
    better: null,
    aggregate: "mean",
    groupings: [["time"]],
    filters: [],
  },
  peak_online: {
    signals: ["presence_gauge"],
    zone: "audience",
    unit: "count",
    better: "up",
    aggregate: "peak",
    groupings: [["time"]],
    filters: [],
  },
  online_share: {
    signals: ["presence_gauge"],
    zone: "audience",
    unit: "ratio",
    better: "up",
    aggregate: "share",
    groupings: [["time"]],
    filters: [],
  },
  segment_members: {
    signals: ["member_events", "messages", "voice"],
    zone: "audience",
    unit: "count",
    better: null,
    aggregate: "last",
    groupings: [["segment"], ["time", "segment"]],
    filters: ["roles", "channels"],
    bounded: true,
  },
  participants: {
    signals: ["messages", "voice"],
    zone: "audience",
    unit: "count",
    better: "up",
    aggregate: "distinct",
    groupings: [["participation"], ["time", "participation"]],
    filters: ["roles", "channels"],
  },
  welcomed_share: {
    signals: ["member_events", "messages"],
    zone: "audience",
    unit: "ratio",
    better: "up",
    aggregate: "share",
    groupings: [["time"]],
    filters: ["roles"],
  },
  command_invocations: {
    signals: ["messages"],
    zone: "audience",
    unit: "count",
    better: null,
    aggregate: "sum",
    groupings: [["locale"]],
    filters: ["roles", "channels"],
  },
} as const satisfies Record<string, MetricEntry>;

export const ANALYTICS_RESOURCE_SIGNALS = {
  retention: ["member_events"],
  activation: ["member_events", "messages", "voice"],
  role_acquisition: ["member_events", "member_roster"],
  leaver_tenure: ["member_events"],
} as const satisfies Record<string, CaptureSignals>;

export type AnalyticsMetric = keyof typeof ANALYTICS_METRICS;

export const ANALYTICS_METRIC_NAMES = Object.keys(ANALYTICS_METRICS) as readonly AnalyticsMetric[];

const isAnalyticsMetric = (name: string): name is AnalyticsMetric =>
  Object.hasOwn(ANALYTICS_METRICS, name);

export const metricEntry = (metric: AnalyticsMetric): MetricEntry => ANALYTICS_METRICS[metric];

export const metricZone = (name: string): AnalyticsZone | undefined =>
  isAnalyticsMetric(name) ? metricEntry(name).zone : undefined;

export const canonicalGrouping = (
  dimensions: readonly MetricDimension[],
): readonly MetricDimension[] =>
  METRIC_DIMENSIONS.filter((dimension) => dimensions.includes(dimension));

const sameGrouping = (left: readonly MetricDimension[], right: readonly MetricDimension[]) =>
  left.length === right.length && left.every((dimension, index) => right[index] === dimension);

const groupsBy = (metric: AnalyticsMetric, dimensions: readonly MetricDimension[]): boolean =>
  dimensions.length === 0 ||
  metricEntry(metric).groupings.some((grouping) =>
    sameGrouping(grouping, canonicalGrouping(dimensions)),
  );

export const rankedDimension = (
  dimensions: readonly MetricDimension[],
): RankedDimension | undefined => {
  const ranked = RANKED_DIMENSIONS.filter((dimension) => dimensions.includes(dimension));
  return ranked.length === 1 ? ranked[0] : undefined;
};

type MetricFilterParam = (typeof METRIC_FILTER_PARAMS)[MetricFilter][number];

export type MetricRead = {
  readonly metrics: readonly AnalyticsMetric[];
  readonly groupBy?: readonly MetricDimension[] | undefined;
  readonly limit?: number | undefined;
  readonly since?: string | undefined;
  readonly until?: string | undefined;
} & { readonly [param in MetricFilterParam]?: unknown };

export const metricReadFilters = (read: MetricRead): readonly MetricFilter[] =>
  METRIC_FILTERS.filter((filter) =>
    METRIC_FILTER_PARAMS[filter].some((param) => read[param] !== undefined),
  );

export const metricReadGrouping = (read: MetricRead): readonly MetricDimension[] =>
  canonicalGrouping(read.groupBy === undefined ? [] : read.groupBy);

const groupingText = (grouping: readonly MetricDimension[]): string =>
  grouping.length === 0 ? "nothing" : grouping.join(",");

const unanswerable = (metric: AnalyticsMetric, read: MetricRead): string | null => {
  const entry = metricEntry(metric);
  const grouping = metricReadGrouping(read);
  if (!groupsBy(metric, grouping)) {
    return `groupBy: ${metric} cannot be grouped by ${groupingText(grouping)}; it groups by ${entry.groupings.map(groupingText).join(" or ")}`;
  }
  const unhonoured = metricReadFilters(read).filter((filter) => !entry.filters.includes(filter));
  if (unhonoured.length > 0) {
    return `${metric} cannot be narrowed by ${unhonoured.join(" or ")}`;
  }
  if (
    entry.bounded === true &&
    grouping.includes("time") &&
    (read.since === undefined || read.until === undefined)
  ) {
    return `${metric} is counted as of each bucket's end, so grouping it by time needs since and until`;
  }
  return null;
};

const unranked = (read: MetricRead): string | null => {
  const grouping = metricReadGrouping(read);
  const ranked = rankedDimension(grouping);
  if (read.limit !== undefined && ranked === undefined) {
    return `limit: ranks a grouping by ${RANKED_DIMENSIONS.join(", ")}, and ${groupingText(grouping)} has none`;
  }
  if (read.limit === undefined && ranked !== undefined && grouping.includes("time")) {
    return `limit: a grouping of time by ${ranked} is cut to its leaders, so it needs a limit`;
  }
  if (read.limit === undefined && ranked === "member") {
    return "limit: a grouping by member answers one point per member, so it needs a limit";
  }
  return null;
};

export const metricReadRefusal = (read: MetricRead): string | null => {
  for (const metric of read.metrics) {
    const wrong = unanswerable(metric, read);
    if (wrong !== null) return wrong;
  }
  return unranked(read);
};

const isMemberRead = (read: {
  readonly groupBy: readonly MetricDimension[];
  readonly filters: readonly MetricFilter[];
}): boolean => read.groupBy.includes("member") || read.filters.includes("member");

export const metricReadZones = (read: {
  readonly metrics: readonly string[];
  readonly groupBy: readonly MetricDimension[];
  readonly filters: readonly MetricFilter[];
}): readonly AnalyticsZone[] => [
  ...new Set([
    ...read.metrics.flatMap((name) => {
      const zone = metricZone(name);
      return zone === undefined ? [] : [zone];
    }),
    ...(isMemberRead(read) ? [MEMBER_ZONE] : []),
  ]),
];
